import React, { useEffect, useState, useCallback } from "react";
import { ClockCounterClockwise, EnvelopeSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

export default function AuditLogPanel({ rules }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ruleFilter, setRuleFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = ruleFilter && ruleFilter !== "all" ? `?rule_id=${ruleFilter}` : "";
      const { data } = await api.get(`/v1/alert-rules/audit${params}`);
      setLogs(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Errore caricamento audit"));
    } finally { setLoading(false); }
  }, [ruleFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-[#111827] border border-slate-800 rounded-lg" data-testid="audit-log-panel">
      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={18} weight="duotone" className="text-[#3EB6F0]" />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">
            Storico regole scattate ({logs.length})
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={ruleFilter} onValueChange={setRuleFilter}>
            <SelectTrigger className="h-8 w-56 bg-[#0a0f1c] border-slate-800 font-mono text-xs" data-testid="audit-rule-filter">
              <SelectValue placeholder="Tutte le regole" />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-800">
              <SelectItem value="all" className="text-xs font-mono">Tutte le regole</SelectItem>
              {(rules || []).map((r) => (
                <SelectItem key={r.id} value={r.id} className="text-xs font-mono">{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="audit-refresh-button"
            className="border-slate-700 hover:bg-slate-800 gap-1 h-8">
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
          </Button>
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500 uppercase tracking-widest sticky top-0 bg-[#111827]">
            <tr className="border-b border-slate-800">
              <th className="text-left px-4 py-2 font-mono">Timestamp</th>
              <th className="text-left px-4 py-2 font-mono">Regola</th>
              <th className="text-left px-2 py-2 font-mono">Condizione</th>
              <th className="text-right px-2 py-2 font-mono">Osservato</th>
              <th className="text-center px-2 py-2 font-mono">Email</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {logs.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500">Nessuno scatto registrato</td></tr>
            )}
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`audit-row-${l.id}`}>
                <td className="px-4 py-2 text-slate-400">{new Date(l.timestamp).toLocaleString("it-IT")}</td>
                <td className="px-4 py-2 text-slate-200 font-semibold">{l.rule_name}</td>
                <td className="px-2 py-2 text-[#3EB6F0]">{l.metric} {l.operator} {l.threshold}</td>
                <td className="px-2 py-2 text-right text-[#EF4444] font-semibold">{l.observed}</td>
                <td className="px-2 py-2 text-center">
                  {l.email_sent ? (
                    <span className="inline-flex items-center gap-1 text-[#10B981]" title={l.email_to || ""}>
                      <EnvelopeSimple size={12} weight="fill" /> inviata
                    </span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
